"use client";
import { Button } from "@/components/ui/shadcn/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/shadcn/popover";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  FlameIcon,
  InfoIcon,
  MinusIcon,
} from "lucide-react";
import { isPerfect, levelFor } from "./levels";

type Props = {
  title: string;
  score: number;
  detail?: string;
  // The last monthly value stored, such as { score: 4.2, label: "octubre" }
  previous?: { score: number; label: string } | null;
  children: React.ReactNode;
};

export default function KpiTile({
  title,
  score,
  detail,
  previous,
  children,
}: Props) {
  const level = levelFor(score);
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(Math.max(score / 10, 0), 1);

  return (
    <div className="card flex items-center gap-4">
      <div className="relative size-16 shrink-0">
        <svg
          viewBox="0 0 64 64"
          className="size-16 -rotate-90"
          aria-hidden="true"
        >
          <circle
            cx="32"
            cy="32"
            r={radius}
            fill="none"
            strokeWidth="6"
            className="stroke-gray-200"
          />
          {/* A round cap would still draw a dot for a score of 0 */}
          {progress > 0 && (
            <circle
              cx="32"
              cy="32"
              r={radius}
              fill="none"
              strokeWidth="6"
              strokeLinecap="round"
              stroke={level.color}
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - progress)}
            />
          )}
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-lg font-semibold tabular-nums">
          {score.toFixed(1)}
        </span>
        {isPerfect(score) && (
          <FlameIcon
            className="absolute -right-1 -top-1 size-5 fill-orange-400 text-orange-600"
            aria-hidden="true"
          />
        )}
      </div>
      <div className="flex-1">
        <h2 className="text-balance text-sm font-semibold">
          {title}
          <span className="sr-only">
            : {score.toFixed(1)} de 10, {level.label}
            {isPerfect(score) && ", excelente"}
          </span>
        </h2>
        {detail && (
          <p className="text-balance text-[0.6875rem] leading-tight text-muted-foreground">
            {detail}
          </p>
        )}
        {previous && <Change score={score} previous={previous} />}
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Más información sobre ${title}`}
          >
            <InfoIcon className="size-5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="space-y-2 text-balance text-sm">
          <p className="font-semibold">
            {score.toFixed(1)}/10 · {level.label}
            {isPerfect(score) && (
              <FlameIcon
                className="ml-1 inline size-4 fill-orange-400 align-text-bottom text-orange-600"
                aria-hidden="true"
              />
            )}
          </p>
          {children}
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Compares the rounded values, so the change matches the numbers on screen
const Change = ({
  score,
  previous,
}: {
  score: number;
  previous: { score: number; label: string };
}) => {
  const change =
    Math.round(score * 10) / 10 - Math.round(previous.score * 10) / 10;
  const rounded = Math.round(change * 10) / 10;
  const Icon =
    rounded > 0 ? ArrowUpIcon : rounded < 0 ? ArrowDownIcon : MinusIcon;
  const color =
    rounded > 0
      ? "text-green-700"
      : rounded < 0
        ? "text-red-700"
        : "text-muted-foreground";

  return (
    <p className={`flex items-center gap-0.5 text-xs font-medium ${color}`}>
      <Icon className="size-3" aria-hidden="true" />
      {rounded > 0 ? "+" : rounded < 0 ? "−" : ""}
      {Math.abs(rounded).toFixed(1)}
      <span className="font-normal text-muted-foreground">
        &nbsp;vs. {previous.label}
      </span>
    </p>
  );
};
